import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';

import { ButtonComp } from 'atoms/Buttons';
import ImageComponent from 'atoms/Image';

import {
  LOCAL_STORAGE_KEYS,
  SHARED_CONSTANTS,
  ROUTE_URL,
} from 'common/constants/SharedConstants';
import { TRANSLATE_MAPPING_KEY } from 'common/constants/i18nTranslateKeys';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';
import {
  isOrderCompletedOnFulfillmentDateTime,
  isOrderProcessCompleted,
  isOrderCancelledOrRefund,
  isSentPosCompletedOrder,
} from 'common/utilities/orderStatusUtils';
import {
  isCateringDisposition,
  isDeliveryDisposition,
  isValidValue,
  getFullImagePath,
  displayPrice,
  redirectToHelpPage,
  isPickupDisposition,
  getMainImagePath,
  redirectToCateringPage,
  getTernaryValue,
} from 'common/utilities/utils';
import { getOrderingDateTime, onTrackOrderBtnClickHandler } from 'organisms/orderHistory/utils/orderHistoryUtils';
import START_ORDER_CONSTANTS from 'organisms/StartOrderComponent/Constants/startOrderConstants';
import { localisationStartAnalytics } from 'common/utilities/analyticsUtils';

export const getOrderItemJSX = params => {
  return (
    <div className='order-history-order-item col-12'>
      {getOrderItemHeaderJSX(params)}
      {getOrderItemBodyJSX(params)}
      {getOrderItemFooterJSX(params)}
    </div>
  );
};

const getAccordionArrowImageClass = params => {
  const { orderHistoryDetails, orderDetails } = params;
  const { orderId } = orderDetails;
  const { isDarkMode, selectedOrderId } = orderHistoryDetails;
  return selectedOrderId === orderId
    ? (( isDarkMode
      && IMAGE_PATH?.AccordianUpDark
      ) || IMAGE_PATH?.ArrowOrderUp)
    : (( isDarkMode
      && IMAGE_PATH?.AccordianDownDark
      ) || IMAGE_PATH?.ArrowOrderDown );
};

const getCurrentOrderStatusImage = orderDetails => {
  const {
    requestedDateTime: { isForNow },
  } = orderDetails;
  if (isOrderCancelledOrRefund(orderDetails)) {
    return IMAGE_PATH?.Reload;
  }
  if (
    isCateringDisposition(orderDetails?.service) ||
    (!isForNow && isSentPosCompletedOrder(orderDetails?.orderStatus))
  ) {
    return IMAGE_PATH?.ScheduledIcon;
  }
  if (
    isDeliveryDisposition(orderDetails?.service) ||
    isPickupDisposition(orderDetails?.service) ||
    (!isForNow && isSentPosCompletedOrder(!orderDetails?.orderStatus))
  ) {
    return IMAGE_PATH?.Time_Clock;
  }
};

const getCurrentOrderStatusMessage = orderDetails => {
  const {
    requestedDateTime: { isForNow },
  } = orderDetails;
  if (isOrderCancelledOrRefund(orderDetails)) {
    return TRANSLATE_MAPPING_KEY?.REFUND_INIT_TEXT;
  }
  if (
    isCateringDisposition(orderDetails?.service) ||
    (!isForNow && isSentPosCompletedOrder(orderDetails?.orderStatus))
  ) {
    return TRANSLATE_MAPPING_KEY?.SCHEDULE;
  }
  if (
    isDeliveryDisposition(orderDetails?.service) ||
    isPickupDisposition(orderDetails?.service) ||
    (!isForNow && isSentPosCompletedOrder(!orderDetails?.orderStatus))
  ) {
    return TRANSLATE_MAPPING_KEY?.IN_PROGRESS;
  }
};

const getHeaderTitle = orderDetails => {
  const { favoriteOrderName, service } = orderDetails;
  if (
    !isOrderProcessCompleted(orderDetails) &&
    !isOrderCompletedOnFulfillmentDateTime(orderDetails) &&
    isOrderCancelledOrRefund(orderDetails)
  ) {
    return translateWithI18Next(TRANSLATE_MAPPING_KEY?.PROCESSING_REFUND)?.toUpperCase();
  }
  if (isValidValue(favoriteOrderName)) {
    return favoriteOrderName;
  }
  return `${translateWithI18Next(service)} ${translateWithI18Next(TRANSLATE_MAPPING_KEY?.ORDER)?.toUpperCase()}`;
};

const getTrackOrderButton = (orderDetails, pickupTrackOrderButtonLabel) => {
  if (isCateringDisposition(orderDetails?.service)) {
    return TRANSLATE_MAPPING_KEY?.VIEW_DETAILS;
  }
  if (isDeliveryDisposition(orderDetails?.service)) {
    return TRANSLATE_MAPPING_KEY?.TRACK_ORDER;
  }
  if (isPickupDisposition(orderDetails?.service)) {
    return translateWithI18Next(pickupTrackOrderButtonLabel);
  }
  return null;
};

export const getOrderingDateTimeJSX = params => {
  const { orderDetails } = params;
  const { occasionTime } = orderDetails;
  return (
    <span className={`order-history-order-date-time ${isOrderCancelledOrRefund(orderDetails) ? 'red' : ''}`}>
      {occasionTime ? occasionTime : getOrderingDateTime(params)}
    </span>
  );
};

export const getOrderItemHeaderJSX = params => {
  const { orderHistoryDetails, updateSetOrderHistoryDetails } = params;
  const orderDetails = {
    ...params?.orderDetails,
    enableGlobalOrderTracking: orderHistoryDetails?.enableGlobalOrderTracking,
    enableRetryCountForPickupOrderTracking: orderHistoryDetails?.enableRetryCountForPickupOrderTracking,
  };
  const { orderId } = orderDetails;
  const { isMobileView, selectedOrderId, pickupTrackOrderButtonLabel } = orderHistoryDetails;

  return (
    <div className='order-history-order-item-header-container col-12'>
      <div
        className={`order-history-order-item-header-left-container ${getTernaryValue(isMobileView, 'col-5', 'col-7')}`}
      >
        {!isOrderProcessCompleted(orderDetails) && !isOrderCompletedOnFulfillmentDateTime(orderDetails) && (
          <div className='order-history-order-item-heading-section'>
            <img
              alt='reload'
              src={getCurrentOrderStatusImage(orderDetails)}
              className='order-item-heading-status-icon'
            />
            {!isMobileView && (
              <span className='order-item-heading-status-msg'>
                {translateWithI18Next(getCurrentOrderStatusMessage(orderDetails))?.toUpperCase()}
              </span>
            )}
          </div>
        )}

        <span className='order-history-order-item-heading'>{getHeaderTitle(orderDetails)}</span>
      </div>
      <div className='order-history-order-item-header-right-container'>
        {!isOrderProcessCompleted(orderDetails) &&
        !isOrderCompletedOnFulfillmentDateTime(orderDetails) &&
        getTrackOrderButton(orderDetails, pickupTrackOrderButtonLabel) ? (
          <ButtonComp
            className='button transparent-btn order-history-track-btn'
            data-testid={`track-order-btn-${orderId}`}
            aria-label={translateWithI18Next(getTrackOrderButton(orderDetails, pickupTrackOrderButtonLabel))}
            onClickHandler={() => onTrackOrderBtnClickHandler(params)}
          >
            <span>{translateWithI18Next(getTrackOrderButton(orderDetails, pickupTrackOrderButtonLabel))}</span>
          </ButtonComp>
        ) : (
          getOrderingDateTimeJSX(params)
        )}

        <img
          alt='arrows'
          src={getAccordionArrowImageClass(params)}
          onClick={() =>
            updateSetOrderHistoryDetails({
              selectedOrderId: selectedOrderId === orderId ? '' : orderId,
              showViewReceiptModal: false,
              showFavoriteModal: false,
              showReorderModal: false,
            })
          }
          className='order-history-order-item-accordion-arrow'
          data-testid={`order-item-arrow-${orderId}`}
        />
      </div>
    </div>
  );
};

export const getRefundOrderDetailJSX = params => {
  const { orderHistoryDetails } = params;
  const { props } = orderHistoryDetails;

  return (
    <div className='global-accordian-header-container mobile-refund-section'>
      <span className='global-accordian-header'>
        <span className='order-refund-msg'>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.REFUND_MSG)}</span>
      </span>
      <div className='global-accordian-right'>
        <ButtonComp
          className='button transparent-btn'
          aria-label={''}
          data-testid='get-help-btn'
          type='submit'
          onClickHandler={() => redirectToHelpPage(props)}
        >
          <span>{translateWithI18Next(TRANSLATE_MAPPING_KEY?.GET_HELP)}</span>
        </ButtonComp>
      </div>
    </div>
  );
};

export const getOrderItemBodyJSX = params => {
  const { orderHistoryDetails, orderDetails, imageOrderContainerSize } = params;
  const { height, width } = imageOrderContainerSize;
  const { orderId, filteredFoodLines, orderedItemDetails } = orderDetails;
  const { selectedOrderId, currencySymbol, currencyConversionFactor } = orderHistoryDetails;
  const listOfFoodLines = orderedItemDetails || filteredFoodLines;

  return (
    selectedOrderId === orderId && (
      <div className='order-history-order-item-content col-12'>
        {listOfFoodLines?.map(food => {
          const { dname, quantity, unitPrice, amount, content } = food?.item || food;
          return (
            <div className='order-history-order-item-panel default-img'>
              <ImageComponent
                className={`order-history-order-item-img-sopac ${!content && 'no-image'}`}
                defaultPath={getFullImagePath(null, orderHistoryDetails, true)}
                srcFile={getMainImagePath(content, width, height)}
              />
              <div className='order-history-order-item-details-section'>
                <span className='order-history-order-item-title'>
                  {quantity} x {dname?.[0]?.value}
                </span>
                <span className='order-history-order-item-price'>
                  {displayPrice(unitPrice || amount, { currencySymbol, currencyConversionFactor })}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    )
  );
};

export const getOrderItemFooterJSX = params => {
  const { orderHistoryDetails, updateSetOrderHistoryDetails, orderDetails, userParams } = params;
  const { orderId, isOrderSavedAsFavorite, service } = orderDetails;
  const { selectedOrderId, props, userOrderStateDispatch, cartDataService } = orderHistoryDetails;
  const { REORDER_MODAL_OPEN, FROM_REORDER_MODAL_OPEN } = START_ORDER_CONSTANTS;
  const getReorderModal = () => {
    !userParams?.localized && localisationStartAnalytics();
    updateSetOrderHistoryDetails({ showReorderModal: true });
    sessionStorage.setItem(LOCAL_STORAGE_KEYS.REORDER_CLICK, true);
    userOrderStateDispatch({ type: FROM_REORDER_MODAL_OPEN, value: { fromReorderFlag: true } });
    userOrderStateDispatch({ type: REORDER_MODAL_OPEN, value: { reorderModalOpen: true } });
  };

  const redirectToCatering = paramProps => {
    if (!cartDataService || isCateringDisposition(cartDataService)) {
      redirectToCateringPage(paramProps);
    } else {
      userOrderStateDispatch({
        type: SHARED_CONSTANTS?.UPDATE_CATERING_RETAIL_MENU_ALERT,
        value: {
          flag: true,
          pathname: ROUTE_URL?.CATERING_PAGE,
          title: TRANSLATE_MAPPING_KEY?.CATERING_NAVALERT_TITLE,
          contentMsg: TRANSLATE_MAPPING_KEY?.CATERING_NAVALERT_SUBTITLE,
        },
      });
    }
  };

  return (
    selectedOrderId === orderId && (
      <div className='order-history-order-item-footer-container col-12'>
        <div className='order-history-order-item-header-left-container'>
          <span
            className='order-history-order-receipt-link'
            data-testid='view-receipt'
            onClick={() => updateSetOrderHistoryDetails({ showViewReceiptModal: true })}
          >
            {translateWithI18Next(TRANSLATE_MAPPING_KEY?.VIEW_RECEIPT)}
          </span>
          <span
            className='order-history-order-occasion-link'
            data-testid='view-occasion-modal'
            onClick={() =>
              isOrderSavedAsFavorite
                ? orderHistoryDetails?.props?.updateMenu(TRANSLATE_MAPPING_KEY?.FAVORITE_ORDERS)
                : updateSetOrderHistoryDetails({ showFavoriteModal: true })
            }
          >
            <img
              alt='favourite-unfavourite'
              src={isOrderSavedAsFavorite ? IMAGE_PATH?.FavouriteActiveBlack : IMAGE_PATH?.FavouriteInActiveBlack}
              className='order-history-order-favourite'
            />
            {translateWithI18Next(
              isOrderSavedAsFavorite
                ? TRANSLATE_MAPPING_KEY?.VIEW_IN_FAVOURITES
                : TRANSLATE_MAPPING_KEY?.ADD_TO_OCCASION,
            )}
          </span>
        </div>

        <div className='order-history-order-item-footer-right-container'>
          <ButtonComp
            className={`button re-order-btn ${isCateringDisposition(service) ? '' : 'redButton'}`}
            aria-label={''}
            type='submit'
            data-testid='re-order-btn'
            onClick={() => {
              isCateringDisposition(service) ? redirectToCatering(props) : getReorderModal();
            }}
          >
            <span>
              {translateWithI18Next(
                isCateringDisposition(service)
                  ? TRANSLATE_MAPPING_KEY?.EXPLORE_CATERING
                  : TRANSLATE_MAPPING_KEY?.REORDER_TEXT,
              )}
            </span>
          </ButtonComp>
        </div>
      </div>
    )
  );
};
